import { CATEGORY_LABELS, DEFAULT_NATURE, expenseGroupOf } from './finance';
import {
  detectDayFirst,
  normalizeText,
  parseImportDate,
  parseImportNumber,
} from './importer';
import { SHARED_LOCATION_ID, type ExpenseCategory, type PurchaseDocumentType } from './types';
import { expenseInputSchema, type ExpenseInput } from './validation';

/**
 * Importador de gastos por archivo (CSV / Excel), 7 sep 2026.
 *
 * Hasta ahora los gastos entraban a mano, por el escaner de facturas o por la
 * periodificacion de impuestos. Un grupo con dos restaurantes que lleva la
 * contabilidad en un Excel (o que exporta el libro de facturas de su gestor)
 * necesitaba teclearlo todo. Misma mecanica que el importador de ventas:
 * mapeo de columnas, previsualizacion y nada entra sin que el usuario lo vea.
 *
 * Decisiones:
 *  - Todo el archivo va al local seleccionado arriba, salvo que el archivo
 *    traiga una columna «Local»: entonces cada fila va al suyo, y «Grupo» o
 *    «Comun» marca un gasto comun a todos los locales.
 *  - La categoria se reconoce por el nombre de nuestra categoria o por
 *    palabras clave (alquiler, luz, nomina...). Lo que no se reconoce cae en
 *    la categoria por defecto que elija el usuario, y se avisa.
 *  - Importe sin IVA o con IVA, segun la columna que se marque; con IVA se
 *    descuenta con el % de la fila (o el de por defecto).
 */

export type ExpenseImportTarget =
  | 'ignore'
  | 'date'
  | 'dueDate'
  | 'paymentDate'
  | 'concept'
  | 'supplier'
  | 'invoiceNumber'
  | 'category'
  | 'subgroup'
  | 'net'
  | 'gross'
  | 'vatPct'
  | 'paymentType'
  | 'paid'
  | 'location'
  | 'notes';

export const EXPENSE_IMPORT_TARGET_LABELS: Record<ExpenseImportTarget, string> = {
  ignore: 'No importar',
  date: 'Fecha',
  dueDate: 'Vencimiento',
  paymentDate: 'Fecha de pago',
  concept: 'Concepto',
  supplier: 'Proveedor',
  invoiceNumber: 'Nº de factura',
  category: 'Categoría',
  subgroup: 'Subgrupo',
  net: 'Importe sin IVA',
  gross: 'Importe con IVA',
  vatPct: 'IVA (%)',
  paymentType: 'Condición de pago (contado / crédito)',
  paid: 'Pagado (sí / no)',
  location: 'Local',
  notes: 'Notas',
};

/** Orden en que se ofrecen en el desplegable. */
export const EXPENSE_IMPORT_TARGETS: ExpenseImportTarget[] = [
  'ignore',
  'date',
  'concept',
  'supplier',
  'invoiceNumber',
  'category',
  'subgroup',
  'net',
  'gross',
  'vatPct',
  'paymentType',
  'dueDate',
  'paid',
  'paymentDate',
  'location',
  'notes',
];

interface Rule {
  words: string[];
  target: ExpenseImportTarget;
}

/** Reglas en orden de prioridad: la primera que casa gana. */
const RULES: Rule[] = [
  { words: ['vencimiento', 'vence', 'due'], target: 'dueDate' },
  { words: ['fecha de pago', 'fecha pago', 'pagado el', 'payment date', 'paid at'], target: 'paymentDate' },
  { words: ['fecha', 'date', 'dia', 'emision'], target: 'date' },
  { words: ['n factura', 'num factura', 'numero', 'factura n', 'invoice', 'nº', 'ref'], target: 'invoiceNumber' },
  { words: ['proveedor', 'supplier', 'vendor', 'acreedor', 'emisor'], target: 'supplier' },
  { words: ['subgrupo', 'subcategoria', 'subgroup'], target: 'subgroup' },
  { words: ['categoria', 'category', 'cuenta', 'partida', 'tipo de gasto', 'clasificacion'], target: 'category' },
  // Los importes van ANTES que el IVA: «Importe sin IVA» es el neto, no el tipo.
  { words: ['base', 'sin iva', 'neto', 'net', 'imponible', 'subtotal'], target: 'net' },
  { words: ['con iva', 'total', 'bruto', 'gross', 'importe', 'amount'], target: 'gross' },
  { words: ['iva', 'vat', 'impuesto', 'tax'], target: 'vatPct' },
  { words: ['condicion', 'forma de pago', 'credito', 'contado', 'payment type', 'plazo'], target: 'paymentType' },
  { words: ['pagado', 'pagada', 'paid', 'estado'], target: 'paid' },
  { words: ['local', 'centro', 'restaurante', 'sede', 'location', 'tienda'], target: 'location' },
  { words: ['concepto', 'descripcion', 'detalle', 'concept', 'description', 'gasto'], target: 'concept' },
  { words: ['nota', 'observacion', 'comentario', 'notes'], target: 'notes' },
];

/** Destinos que solo pueden ir en una columna. */
const SINGLE: Set<ExpenseImportTarget> = new Set([
  'date', 'dueDate', 'paymentDate', 'concept', 'supplier', 'invoiceNumber', 'category',
  'subgroup', 'net', 'gross', 'vatPct', 'paymentType', 'paid', 'location',
]);

/** Adivina el destino de cada columna por su cabecera. */
export function guessExpenseColumns(headers: string[]): ExpenseImportTarget[] {
  const taken = new Set<ExpenseImportTarget>();
  const guesses = headers.map<ExpenseImportTarget>((header) => {
    const h = normalizeText(header);
    if (h === '') return 'ignore';
    const tokens = new Set(h.split(' '));
    for (const rule of RULES) {
      if (!rule.words.some((w) => h === w || tokens.has(w) || (w.length > 3 && h.includes(w)))) continue;
      if (SINGLE.has(rule.target) && taken.has(rule.target)) continue;
      taken.add(rule.target);
      return rule.target;
    }
    return 'ignore';
  });
  // «IVA» como importe y «Base» juntos: el IVA es un importe, no un %. Como
  // no se sabe, se deja al usuario; solo se evita net + gross a la vez.
  if (guesses.includes('net') && guesses.includes('gross')) {
    return guesses.map((g) => (g === 'gross' ? 'ignore' : g));
  }
  return guesses;
}

/* ------------------------------------------------------------------ */
/* Categoria por texto                                                 */
/* ------------------------------------------------------------------ */

/** Palabras clave por categoria, ademas del nombre literal de la categoria. */
const CATEGORY_KEYWORDS: Array<[ExpenseCategory, string[]]> = [
  ['compras_materia_prima', ['materia prima', 'compras', 'genero', 'mercaderia', 'alimentos', 'bebidas', 'carne', 'pescado', 'verdura', 'fruta', 'pan', 'lacteos', 'vino', 'cerveza', 'food', 'beverage', 'insumos alimentarios']],
  ['otros_insumos', ['insumos', 'desechables', 'envases', 'limpieza', 'menaje', 'utensilios', 'supplies']],
  ['sueldos_salarios', ['sueldo', 'salario', 'nomina', 'personal', 'payroll', 'wages', 'plantilla']],
  ['cuotas_ss', ['seguridad social', 'ss', 'cuotas', 'imss', 'infonavit', 'aportaciones', 'social security']],
  ['alquiler', ['alquiler', 'renta', 'arrendamiento', 'rent', 'lease']],
  ['reparaciones', ['reparacion', 'mantenimiento', 'conservacion', 'repair', 'maintenance', 'averia']],
  ['honorarios', ['honorarios', 'gestoria', 'asesoria', 'abogado', 'consultoria', 'contable', 'fees']],
  ['seguros', ['seguro', 'poliza', 'insurance']],
  ['comisiones_bancarias', ['comision', 'banco', 'bancaria', 'tpv', 'datafono', 'bank fee', 'stripe']],
  ['uniformidad', ['uniforme', 'ropa', 'vestuario', 'uniform']],
  ['publicidad_rrss', ['publicidad', 'marketing', 'redes', 'rrss', 'instagram', 'facebook', 'ads', 'promocion']],
  ['comunicacion_web', ['web', 'pagina', 'dominio', 'hosting', 'software', 'app', 'suscripcion', 'saas']],
  ['suministros_agua', ['agua', 'water']],
  ['suministros_electricidad', ['luz', 'electricidad', 'electrica', 'energia', 'electricity', 'power']],
  ['suministros_gas', ['gas', 'butano', 'propano']],
  ['servicios_varios', ['servicios', 'lavanderia', 'limpieza externa', 'extintores', 'plagas', 'desratizacion', 'services']],
  ['telefonia', ['telefono', 'telefonia', 'movil', 'internet', 'fibra', 'phone']],
  ['papeleria', ['papeleria', 'oficina', 'material de oficina', 'stationery']],
  ['otros_gastos', ['otros', 'varios', 'other', 'misc']],
  ['amortizaciones', ['amortizacion', 'depreciacion', 'depreciation']],
  ['intereses', ['intereses', 'financieros', 'prestamo', 'interest', 'loan']],
  ['impuesto_ganancias', ['impuesto sobre', 'sociedades', 'irpf', 'isr', 'renta', 'income tax']],
];

const CATEGORY_BY_LABEL = new Map<string, ExpenseCategory>(
  (Object.entries(CATEGORY_LABELS) as Array<[ExpenseCategory, string]>).flatMap(([key, label]) => [
    [normalizeText(label), key],
    [normalizeText(key.replace(/_/g, ' ')), key],
  ]),
);

/** Categoria a partir del texto de una celda. undefined = no se reconoce. */
export function parseExpenseCategory(raw: string | undefined): ExpenseCategory | undefined {
  const text = normalizeText(raw ?? '');
  if (text === '') return undefined;
  const literal = CATEGORY_BY_LABEL.get(text);
  if (literal) return literal;
  // Las palabras cortas solo valen enteras: «gas» no puede casar con «gastos».
  const tokens = new Set(text.split(' '));
  for (const [category, words] of CATEGORY_KEYWORDS) {
    if (words.some((w) => text === w || tokens.has(w) || (w.length > 4 && text.includes(w)))) return category;
  }
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Valores sueltos                                                     */
/* ------------------------------------------------------------------ */

const YES = new Set(['si', 's', 'yes', 'y', 'true', '1', 'pagado', 'pagada', 'paid', 'ok', 'x']);
const NO = new Set(['no', 'n', 'false', '0', 'pendiente', 'unpaid', 'impagado', 'impagada', 'debe']);

export function parseImportBoolean(raw: string | undefined): boolean | undefined {
  const text = normalizeText(raw ?? '');
  if (text === '') return undefined;
  if (YES.has(text)) return true;
  if (NO.has(text)) return false;
  return undefined;
}

export function parseImportPaymentType(raw: string | undefined): 'contado' | 'credito' | undefined {
  const text = normalizeText(raw ?? '');
  if (text === '') return undefined;
  if (/credit|credito|aplazad|plazo|\d+ ?d/.test(text)) return 'credito';
  if (/contado|cash|efectivo|inmediat|transfer|tarjeta|domicili|recibo/.test(text)) return 'contado';
  return undefined;
}

/** Palabras que en la columna «Local» significan «comun a todo el grupo». */
const SHARED_WORDS = new Set(['grupo', 'comun', 'todos', 'central', 'general', 'group', 'shared', 'all']);

/* ------------------------------------------------------------------ */
/* Construccion                                                        */
/* ------------------------------------------------------------------ */

export interface ExpenseImportOptions {
  /** Local al que va todo lo que no diga otra cosa. */
  defaultLocationId: string;
  /** Locales activos, para reconocer la columna «Local» por nombre. */
  locations: Array<{ locationId: string; name: string }>;
  /** Proveedores existentes, para emparejar por nombre. */
  suppliers: Array<{ supplierId: string; name: string }>;
  /** Categoria para las filas cuya categoria no se reconoce. */
  defaultCategory: ExpenseCategory;
  /** IVA para las filas sin columna de IVA (o con la celda vacia). */
  defaultVatPct: number;
}

/** Un gasto listo para guardar, mas lo que la pantalla necesita para explicarlo. */
export interface ExpenseImportItem {
  input: ExpenseInput;
  /** Fila del archivo (1 = primera fila de datos). */
  row: number;
  /** Nombre del proveedor tal y como venia, si no existe todavia. */
  newSupplierName?: string;
  /** true si la categoria no se reconocio y se ha usado la de por defecto. */
  categoryGuessed: boolean;
}

export interface ExpenseImportResult {
  items: ExpenseImportItem[];
  skippedRows: number;
  /**
   * Cuales y por que (Kilo, oct 2026). `row` cuenta desde 0 sobre las filas
   * de datos, sin la cabecera, igual que en el importador de ventas.
   */
  skippedDetail: Array<{ row: number; reason: string }>;
  warnings: string[];
  /** Proveedores que no existen y habria que crear (nombres unicos, en orden). */
  newSuppliers: string[];
  /** Total sin IVA de lo que se va a importar. */
  totalNet: number;
  /** Filas cuya categoria se ha tenido que suponer. */
  guessedCategories: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function buildExpenseImport(
  rows: string[][],
  targets: ExpenseImportTarget[],
  options: ExpenseImportOptions,
): ExpenseImportResult {
  const index = (target: ExpenseImportTarget): number => targets.indexOf(target);
  const dateIndex = index('date');
  const netIndex = index('net');
  const grossIndex = index('gross');
  const warnings: string[] = [];
  if (dateIndex === -1 || (netIndex === -1 && grossIndex === -1)) {
    return {
      items: [],
      skippedRows: rows.length,
      skippedDetail: [],
      warnings: ['Hacen falta al menos una columna de fecha y una de importe (sin IVA o con IVA).'],
      newSuppliers: [],
      totalNet: 0,
      guessedCategories: 0,
    };
  }
  const cell = (row: string[], target: ExpenseImportTarget): string | undefined => {
    const i = index(target);
    return i === -1 ? undefined : (row[i] ?? '').trim() || undefined;
  };

  const dayFirst = detectDayFirst(rows.map((r) => r[dateIndex]));
  const supplierByName = new Map(options.suppliers.map((s) => [normalizeText(s.name), s.supplierId]));
  const locationByName = new Map(options.locations.map((l) => [normalizeText(l.name), l.locationId]));
  const newSuppliers = new Map<string, string>(); // normalizado → nombre tal cual

  const items: ExpenseImportItem[] = [];
  let skippedRows = 0;
  const skippedDetail: Array<{ row: number; reason: string }> = [];
  const descartar = (row: number, reason: string) => {
    skippedRows++;
    skippedDetail.push({ row, reason });
  };
  let creditNotes = 0;
  let guessedCategories = 0;
  let unknownLocations = 0;
  let totalNet = 0;

  rows.forEach((row, n) => {
    const date = parseImportDate(row[dateIndex], dayFirst);
    const vatCell = parseImportNumber(cell(row, 'vatPct'));
    // Un IVA escrito como 0,21 es 21 %.
    const vatPct = vatCell == null ? options.defaultVatPct : vatCell > 0 && vatCell < 1 ? round2(vatCell * 100) : vatCell;
    const net = netIndex === -1 ? undefined : parseImportNumber(row[netIndex]);
    const gross = grossIndex === -1 ? undefined : parseImportNumber(row[grossIndex]);
    const amount = net ?? (gross == null ? undefined : round2(gross / (1 + vatPct / 100)));
    if (!date) {
      const crudo = (row[dateIndex] ?? '').trim();
      descartar(n, crudo ? `fecha que no se entiende: «${crudo.slice(0, 30)}»` : 'sin fecha');
      return;
    }
    if (amount == null) {
      descartar(n, 'sin importe legible');
      return;
    }
    if (amount < 0) {
      /*
       * Notas de credito y abonos: el registro de compras de SUNAT las trae en
       * negativo. Alexia no guarda gastos negativos; se dicen por su nombre
       * para que se descuenten del gasto que corrigen y el mes cuadre.
       */
      creditNotes++;
      descartar(n, 'importe negativo (nota de crédito o abono): no se importa; descuéntalo del gasto al que corrige');
      return;
    }
    if (amount === 0) {
      descartar(n, 'importe 0');
      return;
    }

    const categoryRaw = cell(row, 'category');
    const parsedCategory = parseExpenseCategory(categoryRaw);
    // Sin columna de categoria, se intenta con el concepto, que muchas veces
    // la lleva («Alquiler septiembre», «Luz agosto»).
    const conceptRaw = cell(row, 'concept');
    const category =
      parsedCategory ?? (categoryRaw ? undefined : parseExpenseCategory(conceptRaw)) ?? options.defaultCategory;
    const categoryGuessed = parsedCategory == null && category === options.defaultCategory;
    if (categoryGuessed) guessedCategories++;

    const supplierRaw = cell(row, 'supplier');
    let supplierId: string | undefined;
    let newSupplierName: string | undefined;
    if (supplierRaw) {
      const key = normalizeText(supplierRaw);
      supplierId = supplierByName.get(key);
      if (!supplierId) {
        newSupplierName = newSuppliers.get(key) ?? supplierRaw;
        newSuppliers.set(key, newSupplierName);
      }
    }

    let locationId = options.defaultLocationId;
    const locationRaw = cell(row, 'location');
    if (locationRaw) {
      const key = normalizeText(locationRaw);
      if (SHARED_WORDS.has(key)) locationId = SHARED_LOCATION_ID;
      else if (locationByName.has(key)) locationId = locationByName.get(key)!;
      else unknownLocations++;
    }

    const dueDate = parseImportDate(cell(row, 'dueDate'), dayFirst);
    const paymentType = parseImportPaymentType(cell(row, 'paymentType')) ?? (dueDate ? 'credito' : 'contado');
    const paidCell = parseImportBoolean(cell(row, 'paid'));
    const paymentDate = parseImportDate(cell(row, 'paymentDate'), dayFirst);
    const paid = paidCell ?? (paymentDate ? true : paymentType === 'contado');
    const invoiceNumber = cell(row, 'invoiceNumber');
    const subgroupRaw = cell(row, 'subgroup');

    const input: ExpenseInput = {
      locationId,
      paymentType,
      date,
      dueDate: paymentType === 'credito' ? (dueDate && dueDate >= date ? dueDate : date) : undefined,
      paymentDate: paid ? (paymentDate ?? dueDate ?? date) : undefined,
      concept: (conceptRaw ?? supplierRaw ?? CATEGORY_LABELS[category]).slice(0, 160),
      supplierId,
      invoiceNumber: invoiceNumber?.slice(0, 40),
      category,
      subgroup: subgroupRaw && expenseGroupOf(category) ? subgroupRaw.slice(0, 40) : undefined,
      nature: DEFAULT_NATURE[category],
      netAmount: round2(amount),
      vatPct,
      paid,
      invoiceStatus: invoiceNumber ? 'facturado' : 'sin_factura',
      notes: cell(row, 'notes')?.slice(0, 500),
    };
    // Las mismas reglas que la API: una fila que no las cumple se descarta
    // aqui, con su motivo, en vez de hacer que rechacen su tanda entera.
    const valido = expenseInputSchema.safeParse(input);
    if (!valido.success) {
      descartar(n, valido.error.issues[0]?.message ?? 'datos no válidos');
      return;
    }
    totalNet += input.netAmount;
    items.push({ input, row: n + 1, newSupplierName, categoryGuessed });
  });

  if (grossIndex !== -1 && netIndex === -1) {
    warnings.push(
      `Los importes venían con IVA: se les ha quitado el de cada fila (o el ${options.defaultVatPct} % por defecto) para guardarlos sin IVA.`,
    );
  }
  if (creditNotes > 0) {
    warnings.push(
      `${creditNotes} ${creditNotes === 1 ? 'nota de crédito o abono' : 'notas de crédito o abonos'} en negativo no se ${creditNotes === 1 ? 'importa' : 'importan'}: el total de Alexia saldrá más alto que el de tu registro por ese importe hasta que lo descuentes del gasto original.`,
    );
  }
  if (guessedCategories > 0) {
    warnings.push(
      `${guessedCategories} ${guessedCategories === 1 ? 'fila sin categoría reconocida: se guarda' : 'filas sin categoría reconocida: se guardan'} como «${CATEGORY_LABELS[options.defaultCategory]}». Puedes cambiarla después, gasto a gasto.`,
    );
  }
  if (unknownLocations > 0) {
    warnings.push(
      `${unknownLocations} ${unknownLocations === 1 ? 'fila con un local que no existe: va' : 'filas con un local que no existe: van'} al local seleccionado.`,
    );
  }
  return {
    items,
    skippedRows,
    skippedDetail,
    warnings,
    newSuppliers: [...newSuppliers.values()],
    totalNet: round2(totalNet),
    guessedCategories,
  };
}

/* ------------------------------------------------------------------ */
/* Repetidos                                                           */
/* ------------------------------------------------------------------ */

/**
 * Clave de identidad de un gasto importado (9 sep 2026, Alex: «vigilar que no
 * se carguen doble los gastos»).
 *
 * Proveedor, numero de documento e importe sin IVA. Los tres, por esto:
 *
 *  - Sin proveedor NO hay clave. Meter todo lo que no tiene proveedor en un
 *    mismo cubo hacia que dos tickets de proveedores distintos numerados «1»
 *    se dieran por repetidos (revision del 9 sep 2026).
 *  - Sin numero tampoco: dos cajas de fruta del mismo proveedor, el mismo dia
 *    y por el mismo importe son dos gastos de verdad, y saltarse uno callando
 *    seria peor que duplicarlo.
 *  - El importe entra en la clave porque el libro de una gestoria trae UNA
 *    FILA POR BASE DE IVA de la misma factura (300 al 10 % y 120 al 21 %, las
 *    dos como A-2231): con solo proveedor y numero, la segunda linea se
 *    descartaba como «ya estaba» y el mes quedaba corto sin que nada lo
 *    delatara. El coste es que reimportar una fila con el importe CORREGIDO
 *    crea una segunda en vez de pisar la antigua: se ve en la lista y se
 *    borra, que es lo contrario de invisible.
 *
 * El numero se compara con `normalizeText`, como hace el escaner
 * (`buscarDuplicado`): «VB-2231» y «VB 2231» son el mismo documento en las
 * dos puertas, no solo en una.
 */
export function claveDeGasto(gasto: {
  supplierId?: string;
  invoiceNumber?: string;
  netAmount: number;
}): string | undefined {
  if (!gasto.supplierId) return undefined;
  const numero = normalizeText(gasto.invoiceNumber ?? '');
  if (!numero) return undefined;
  return `${gasto.supplierId}|${numero}|${Math.round(gasto.netAmount * 100)}`;
}

export interface RepetidosResult<T> {
  nuevos: T[];
  /** Los que ya estaban guardados o venian repetidos dentro del propio archivo. */
  repetidos: T[];
}

/**
 * Separa lo que se puede importar de lo que ya existe. Mira tanto los gastos
 * ya guardados como las filas anteriores del mismo archivo, que es el otro
 * camino para acabar con la misma factura dos veces.
 */
export function separarRepetidos<T extends { input: { supplierId?: string; invoiceNumber?: string; netAmount: number } }>(
  items: T[],
  guardados: Array<{ supplierId?: string; invoiceNumber?: string; netAmount: number; documentType?: PurchaseDocumentType }>,
): RepetidosResult<T> {
  const vistas = new Set<string>();
  for (const gasto of guardados) {
    // Los albaranes no cuentan: un albaran y la factura que lo cierra llevan a
    // menudo el MISMO numero de documento, y darla por repetida seria dejar
    // fuera justo la factura que hay que contabilizar.
    if (gasto.documentType === 'albaran') continue;
    const clave = claveDeGasto(gasto);
    if (clave) vistas.add(clave);
  }
  const nuevos: T[] = [];
  const repetidos: T[] = [];
  for (const item of items) {
    const clave = claveDeGasto(item.input);
    if (clave && vistas.has(clave)) {
      repetidos.push(item);
      continue;
    }
    if (clave) vistas.add(clave);
    nuevos.push(item);
  }
  return { nuevos, repetidos };
}

/* ------------------------------------------------------------------ */
/* Plantilla                                                           */
/* ------------------------------------------------------------------ */

export const GASTOS_TEMPLATE_HEADERS = [
  'Fecha',
  'Concepto',
  'Proveedor',
  'Nº factura',
  'Categoría',
  'Importe sin IVA',
  'IVA %',
  'Condición de pago',
  'Vencimiento',
  'Pagado',
  'Local',
];

export const GASTOS_TEMPLATE_ROWS: string[][] = [
  ['03/09/2026', 'Alquiler septiembre', 'Inmobiliaria Sol', '', 'Alquiler', '1.800,00', '21', 'Contado', '', 'Sí', ''],
  ['05/09/2026', 'Carne semana 36', 'Carnes Roma', 'A-2231', 'Compras materia prima', '642,30', '10', 'Crédito', '05/10/2026', 'No', ''],
  ['10/09/2026', 'Gestoría trimestre', 'Asesores Norte', 'F-88', 'Honorarios', '350,00', '21', 'Contado', '', 'Sí', 'Grupo'],
];

